import { request } from './client';
import type {
  MergeRequest,
  CreateMergeRequestInput,
  CreateGlobalMergeRequestInput,
  DenyMergeRequestInput,
  BoardPresenceInfo,
  GlobalMergeRequestResult,
  GlobalMergeResult,
  GlobalMergeRequestDetails,
} from '../types';

export const mergeRequestsApi = {
  // Get all merge requests for a board (managers see all, users see their pending requests)
  list: (boardId: string, status?: string): Promise<MergeRequest[]> =>
    request(`/api/squareup/boards/${boardId}/merge-requests${status ? `?status=${status}` : ''}`),

  // Get pending merge requests for the current user on a specific board
  getMyPending: (boardId: string): Promise<MergeRequest[]> =>
    request(`/api/squareup/boards/${boardId}/merge-requests/pending`),

  // Get ALL pending merge requests for the current user (across all boards)
  getAllPending: (): Promise<MergeRequest[]> =>
    request('/api/squareup/merge-requests/pending'),

  // Create a merge request for a single board (manager only)
  create: (boardId: string, input: CreateMergeRequestInput): Promise<MergeRequest> =>
    request(`/api/squareup/boards/${boardId}/merge-requests`, {
      method: 'POST',
      body: input,
    }),

  // Approve a merge request (target user only)
  approve: (boardId: string, requestId: string): Promise<MergeRequest> =>
    request(`/api/squareup/boards/${boardId}/merge-requests/${requestId}/approve`, {
      method: 'POST',
    }),

  // Deny a merge request (target user only)
  deny: (boardId: string, requestId: string, input?: DenyMergeRequestInput): Promise<MergeRequest> =>
    request(`/api/squareup/boards/${boardId}/merge-requests/${requestId}/deny`, {
      method: 'POST',
      body: input || {},
    }),

  // Cancel a merge request (manager who created it)
  cancel: (boardId: string, requestId: string): Promise<void> =>
    request(`/api/squareup/boards/${boardId}/merge-requests/${requestId}`, {
      method: 'DELETE',
    }),

  // =============================================================================
  // Global Merge Request API Methods
  // =============================================================================

  // Get boards where an offline player has squares (for global merge preview)
  // If targetUserId is provided, also returns whether the target is a member of each board
  getOfflinePlayerBoards: (playerId: string, targetUserId?: string): Promise<BoardPresenceInfo[]> =>
    request(`/api/squareup/offline-players/${playerId}/boards${targetUserId ? `?targetUserId=${targetUserId}` : ''}`),

  // Create a global merge request (across all boards where player has squares)
  createGlobal: (playerId: string, input: CreateGlobalMergeRequestInput): Promise<GlobalMergeRequestResult> =>
    request(`/api/squareup/offline-players/${playerId}/merge-all`, {
      method: 'POST',
      body: input,
    }),

  // Approve a global merge request (target user only)
  approveGlobal: (globalRequestId: string): Promise<GlobalMergeResult> =>
    request(`/api/squareup/merge-requests/global/${globalRequestId}/approve`, {
      method: 'POST',
    }),

  // Deny a global merge request (target user only)
  denyGlobal: (globalRequestId: string, input?: DenyMergeRequestInput): Promise<void> =>
    request(`/api/squareup/merge-requests/global/${globalRequestId}/deny`, {
      method: 'POST',
      body: input || {},
    }),

  // Get global merge request details (for target user approval UI)
  getGlobalDetails: (globalRequestId: string): Promise<GlobalMergeRequestDetails> =>
    request(`/api/squareup/merge-requests/global/${globalRequestId}`),

  // Get pending merge requests created by the current user for their offline players
  getMyOutgoingPending: (): Promise<MergeRequest[]> =>
    request('/api/squareup/merge-requests/outgoing'),
};
